/**
 * Countdown Hook
 *
 * Provides real-time countdown to a target date.
 * Updates every second and determines status (upcoming/live/ended).
 */

import { useState, useEffect } from 'react';

export interface TimeLeft {
  status: 'upcoming' | 'live' | 'ended';
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

/**
 * Calculate time remaining until target date
 */
function calculateTimeLeft(targetDate: string): TimeLeft {
  const difference = new Date(targetDate).getTime() - Date.now();

  if (difference <= 0) {
    return { status: 'live' };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((difference / 1000 / 60) % 60);
  const seconds = Math.floor((difference / 1000) % 60);

  return {
    status: 'upcoming',
    days,
    hours,
    minutes,
    seconds,
  };
}

/**
 * Hook that returns time left until target date, updating every second
 *
 * @param targetDate - ISO date string of target date
 * @returns TimeLeft object with days, hours, minutes, seconds and status
 */
export function useCountdown(targetDate: string): TimeLeft {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}
